import { useMemo, useState } from 'react';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

export default function CreditoOverrideModal({
  isOpen,
  onClose,
  onConfirm,
  credit,
  canOverride = false,
}) {
  const [motivo, setMotivo] = useState('');
  const bloqueos = credit?.bloqueos || [];

  const resumen = useMemo(
    () => [
      { label: 'Límite', value: formatCurrency(credit?.limite_credito) },
      { label: 'Saldo actual', value: formatCurrency(credit?.saldo_actual) },
      { label: 'Saldo vencido', value: formatCurrency(credit?.saldo_vencido) },
      { label: 'Saldo proyectado', value: formatCurrency(credit?.saldo_proyectado) },
    ],
    [credit],
  );

  const handleConfirm = () => {
    if (!motivo.trim()) return;
    onConfirm?.(motivo.trim());
    setMotivo('');
  };

  const handleClose = () => {
    setMotivo('');
    onClose?.();
  };

  return (
    <ModalBase isOpen={isOpen} onClose={handleClose} labelledBy="modal-credito">
      <div className="space-y-4 text-sm text-[rgb(var(--fg))]">
        <div>
          <h2 id="modal-credito" className="text-lg font-semibold text-[rgb(var(--fg))]">
            Crédito bloqueado
          </h2>
          <p className="text-[rgba(var(--fg),0.7)]">
            La operación excede el perfil crediticio del cliente.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {resumen.map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.8)] p-3"
            >
              <p className="text-xs uppercase text-[rgba(var(--fg),0.6)]">{item.label}</p>
              <p className="mt-1 font-semibold">{item.value}</p>
            </div>
          ))}
        </div>

        {bloqueos.length > 0 && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
            <p className="font-semibold">Motivos del bloqueo</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {bloqueos.map((item) => (
                <li key={item.code}>{item.message}</li>
              ))}
            </ul>
          </div>
        )}

        {canOverride ? (
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-[rgba(var(--fg),0.6)]">
              Motivo del override
            </label>
            <textarea
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              rows={3}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.9)] px-3 py-2 text-sm"
              placeholder="Detalle la autorización..."
            />
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-700">
            No contás con permisos para autorizar un override de crédito.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cerrar
          </Button>
          {canOverride && (
            <Button onClick={handleConfirm} disabled={!motivo.trim()}>
              Autorizar override
            </Button>
          )}
        </div>
      </div>
    </ModalBase>
  );
}
